import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { FiDownload, FiLoader, FiAlertCircle, FiCheckCircle, FiDatabase, FiLock } from 'react-icons/fi'

// Backup completo do sistema: source (backend/frontend/vault/configs/.env) +
// dump MySQL gzipado, empacotados num ZIP servido pra download.
// Endpoint: GET /api/admin/backup (authMiddleware + adminMiddleware).
// Tempo típico: ~30-60s. Tamanho típico: ~400-500 MB.

type Status = 'idle' | 'generating' | 'success' | 'error'

export function BackupSection() {
  const { authenticatedFetch } = useAuth()
  const [status, setStatus] = useState<Status>('idle')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [confirmed, setConfirmed] = useState(false)

  const handleBackup = async () => {
    if (!confirmed) {
      setConfirmed(true)
      // Auto-reset se não clicar em 8s pra evitar trigger acidental
      setTimeout(() => setConfirmed(c => c), 8000)
      return
    }
    setStatus('generating')
    setErrorMsg(null)
    try {
      // Timeout 10 min: backend leva ~30-40s pra gerar (mysqldump + gzip + zip
      // stream) e o download de ~470 MB depende da rede. Default 30s de
      // authenticatedFetch estoura sempre. Ver AuthContext:242.
      const r = await authenticatedFetch('/api/admin/backup', { timeout: 600000 } as any)
      if (!r.ok) {
        const errText = await r.text().catch(() => '')
        throw new Error(`HTTP ${r.status}: ${errText.substring(0, 100) || r.statusText}`)
      }
      // Extrai filename do Content-Disposition (fallback: timestamp local)
      const dispo = r.headers.get('Content-Disposition') || ''
      const m = /filename="([^"]+)"/.exec(dispo)
      const filename = m ? m[1] : `crm-twms-backup-${new Date().toISOString().slice(0, 10)}.zip`
      const blob = await r.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = filename
      document.body.appendChild(a); a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 1000)
      setStatus('success')
      setConfirmed(false)
      setTimeout(() => setStatus('idle'), 4000)
    } catch (e: any) {
      setStatus('error')
      setErrorMsg(e?.message || 'Erro desconhecido')
      setConfirmed(false)
    }
  }

  return (
    <div className="p-4">
      <div className="flex items-center gap-2 mb-2.5">
        <FiDatabase className="w-4 h-4 text-primary" />
        <span className="text-sm font-semibold text-foreground">Backup do sistema</span>
      </div>

      <button
        onClick={handleBackup}
        disabled={status === 'generating'}
        className={`w-full flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-[12px] font-medium border transition-all ${
          status === 'generating'
            ? 'bg-secondary text-muted-foreground border-border cursor-wait'
            : status === 'success'
              ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
              : confirmed
                ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/40 hover:bg-amber-500/20'
                : 'bg-secondary text-foreground border-border hover:bg-secondary/80'
        }`}
        title={confirmed ? 'Clique de novo pra gerar o ZIP' : 'Backup completo do sistema'}>
        {status === 'generating' ? (
          <><FiLoader className="w-3 h-3 animate-spin" /> Gerando + baixando... (~1-3min)</>
        ) : status === 'success' ? (
          <><FiCheckCircle className="w-3 h-3" /> Baixado!</>
        ) : confirmed ? (
          <><FiAlertCircle className="w-3 h-3" /> Clique pra confirmar</>
        ) : (
          <><FiDownload className="w-3 h-3" /> Backup completo</>
        )}
      </button>

      {/* Disclaimer permanente, sempre visível pra evitar surpresa */}
      <div className="mt-2 text-[9px] text-muted-foreground/70 leading-relaxed">
        <div className="flex items-start gap-1 mb-1">
          <FiLock className="w-2.5 h-2.5 text-amber-500 mt-0.5 shrink-0" />
          <span><span className="font-semibold text-foreground/80">Inclui credenciais</span> do <code className="text-[8px] bg-secondary/60 px-1 rounded">.env</code> e tokens Meta. Armazene offline.</span>
        </div>
        <div className="text-muted-foreground/50">
          Conteúdo: dump MySQL gzipado + source completo (backend, frontend, vault, configs).
          <span className="text-muted-foreground/40"> Exclui: node_modules, mídia WhatsApp, logs.</span>
        </div>
      </div>

      {status === 'error' && (
        <div className="mt-2 flex items-start gap-1.5 p-2 rounded-lg bg-red-500/10 border border-red-500/30">
          <FiAlertCircle className="w-3 h-3 text-red-500 shrink-0 mt-0.5" />
          <div className="text-[10px] text-red-600 dark:text-red-400 leading-tight">
            <div className="font-semibold mb-0.5">Falhou</div>
            <div className="text-[9px] opacity-80 break-all">{errorMsg}</div>
          </div>
        </div>
      )}
    </div>
  )
}
